import React from 'react'
import { Formik, FormikConfig, Form as FormikForm, isFunction } from 'formik'

import { DefaultNavigationConfirmation } from './DefaultNavigationConfirmation'
import { SubmitConfirmation as DefaultSubmitConfirmation, SubmitConfirmationProps } from './SubmitConfirmation'
import type { OnSubmit, OnSubmitSuccess } from './types'
import { isConfirming, useSubmitConfirmation } from './use-submit-confirmation'
import { useSubmitErrorHandling } from './use-submit-error-handling'
import { useSubmitReinitialize } from './use-submit-reinitialize'
import { useOnSubmitSuccess } from './use-on-submit-success'
import { ActionBeforeLogout } from './BaseNavigationConfirmation'

interface NavigationConfirmationProps {
  isPortal?: boolean
}

export interface FormProps<Values, Result extends Values> extends Omit<FormikConfig<Values>, 'onSubmit'> {
  /**
   * Enabling this will show a confirmation modal before the actual
   * form submission is triggered.
   *
   * @default false
   */
  confirmationRequired?: boolean
  /**
   * By default this uses a component that checks if the form is dirty and the path is changing.
   * If you need to overwrite it you can pass in a different component that reuses the base navigation component.
   * If you don't want any navigation confirmation you can pass in `null`.
   */
  NavigationConfirmation?: React.ComponentType<NavigationConfirmationProps> | null
  /**
   * Submission handler
   *
   * This handler is used for handling the form submission mostly this means
   * doing an API call to create or update something.
   */
  onSubmit: OnSubmit<Values, Result>
  /**
   * This way you can add a ID to the form element.
   * This makes it possible to have submit buttons outside the form component.
   */
  formID?: string
  /**
   * Submission success handler
   *
   * This handler is used for showing notifications or navigating
   * to another page after form submission. This handler is triggered after
   * the form state has been reset.
   */
  onSubmitSuccess: OnSubmitSuccess<Values, Result>
  /**
   * If you want something other than the default confirmation dialog you can
   * pass in a custom component.
   */
  SubmitConfirmation?: React.ComponentType<SubmitConfirmationProps>
  /**
   * Set the styling of the form
   */
  style?: React.CSSProperties
  /**
   * handleShopChange to change shop
   */
  handleShopChange?: (shopId: string) => Promise<void>
  /**
   * handleLogout to logout
   */
  handleLogout?: (orderAction: `${ActionBeforeLogout}`) => Promise<void>
  /**
   * this will decide if the shop is B2B or GMS
   */
  isPortal?: boolean
}

export function Form<Values extends object, Result extends Values = Values>({
  children,
  confirmationRequired = false,
  NavigationConfirmation = DefaultNavigationConfirmation,
  formID,
  onSubmit,
  onSubmitSuccess,
  SubmitConfirmation = DefaultSubmitConfirmation,
  style,
  handleShopChange,
  handleLogout,
  isPortal,
  ...props
}: React.PropsWithChildren<FormProps<Values, Result>>) {
  let enhancedSubmit = useSubmitErrorHandling(onSubmit)
  enhancedSubmit = useSubmitReinitialize(enhancedSubmit)
  enhancedSubmit = useOnSubmitSuccess(enhancedSubmit, onSubmitSuccess)
  const submitConfirmation = useSubmitConfirmation(enhancedSubmit, confirmationRequired)

  return (
    <Formik onSubmit={submitConfirmation.handleSubmit} {...props}>
      {formikProps => (
        <>
          {isConfirming(submitConfirmation) ? (
            <SubmitConfirmation
              open={isConfirming(submitConfirmation)}
              onCancel={submitConfirmation.handleCancel}
              onConfirm={submitConfirmation.handleConfirm}
            />
          ) : null}
          {NavigationConfirmation && (
            <DefaultNavigationConfirmation handleShopChange={handleShopChange} handleLogout={handleLogout} isPortal={isPortal} />
          )}
          <FormikForm autoComplete='off' noValidate id={formID} style={style}>
            {isFunction(children) ? children(formikProps) : children}
          </FormikForm>
        </>
      )}
    </Formik>
  )
}
